import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useI18n } from "@/lib/i18n";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "widerruf.cookie_consent.v1";

/**
 * Read the persisted consent.
 * Returns null if the user hasn't decided yet → banner must be shown.
 */
export function getCookieConsent() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

export default function CookieBanner() {
    const { t } = useI18n();
    const [visible, setVisible] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    useEffect(() => {
        const decided = getCookieConsent();
        if (!decided) setVisible(true);
    }, []);

    // Allow re-opening from a footer link via custom event
    useEffect(() => {
        const open = () => { setShowSettings(true); setVisible(true); };
        window.addEventListener("widerruf:open-cookie-settings", open);
        return () => window.removeEventListener("widerruf:open-cookie-settings", open);
    }, []);

    const persist = (consent) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({
                ...consent,
                necessary: true,
                decided_at: new Date().toISOString(),
                version: 1,
            }));
        } catch { /* storage disabled, banner re-appears next visit */ }
        setVisible(false);
        setShowSettings(false);
    };

    const acceptAll = () => persist({ necessary: true, analytics: true });
    const rejectAll = () => persist({ necessary: true, analytics: false });
    const saveSelection = () => persist({ necessary: true, analytics });

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 240, damping: 28 }}
                    className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:pb-6"
                    role="dialog"
                    aria-live="polite"
                    aria-labelledby="cookie-banner-title"
                    data-testid="cookie-banner"
                >
                    <div className="max-w-4xl mx-auto bg-white border border-gray-200 shadow-xl rounded-lg overflow-hidden">
                        <div className="p-5 sm:p-6">
                            <div className="flex items-start gap-4">
                                <div className="hidden sm:flex h-10 w-10 rounded-full bg-[#0b905d]/10 items-center justify-center shrink-0">
                                    <Cookie className="w-5 h-5 text-[#0b905d]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 id="cookie-banner-title" className="font-display text-lg font-semibold text-[#00153f]">
                                        {t("cookie_title")}
                                    </h2>
                                    <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
                                        {t("cookie_text")}{" "}
                                        <Link
                                            to="/datenschutz"
                                            className="underline underline-offset-2 hover:text-[#0b905d]"
                                            data-testid="cookie-privacy-link"
                                        >
                                            {t("cookie_more")}
                                        </Link>
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={rejectAll}
                                    className="text-gray-400 hover:text-gray-700 shrink-0"
                                    aria-label="close"
                                    data-testid="cookie-close"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {showSettings && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="mt-5 border-t border-gray-200 pt-5 space-y-4"
                                    data-testid="cookie-settings"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-[#00153f]">{t("cookie_necessary")}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{t("cookie_necessary_desc")}</div>
                                        </div>
                                        <span className="text-[11px] font-mono uppercase tracking-widest text-[#0b905d] whitespace-nowrap pt-0.5">
                                            {t("cookie_always_on")}
                                        </span>
                                    </div>
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium text-[#00153f]">{t("cookie_analytics")}</div>
                                            <div className="text-xs text-gray-500 mt-0.5">{t("cookie_analytics_desc")}</div>
                                        </div>
                                        <Switch
                                            checked={analytics}
                                            onCheckedChange={setAnalytics}
                                            data-testid="cookie-toggle-analytics"
                                        />
                                    </div>
                                </motion.div>
                            )}

                            <div className="mt-5 flex flex-wrap gap-2 justify-end">
                                {!showSettings && (
                                    <Button
                                        variant="ghost"
                                        className="text-[#00153f] hover:bg-slate-100"
                                        onClick={() => setShowSettings(true)}
                                        data-testid="cookie-settings-btn"
                                    >
                                        {t("cookie_settings")}
                                    </Button>
                                )}
                                <Button
                                    variant="outline"
                                    className="border-slate-300 text-[#00153f] hover:bg-slate-100"
                                    onClick={rejectAll}
                                    data-testid="cookie-reject-btn"
                                >
                                    {t("cookie_reject")}
                                </Button>
                                {showSettings ? (
                                    <Button
                                        className="bg-[#00153f] hover:bg-[#001a4d] text-white"
                                        onClick={saveSelection}
                                        data-testid="cookie-save-btn"
                                    >
                                        {t("cookie_save")}
                                    </Button>
                                ) : (
                                    <Button
                                        className="bg-[#0b905d] hover:bg-[#067a4d] text-white"
                                        onClick={acceptAll}
                                        data-testid="cookie-accept-btn"
                                    >
                                        {t("cookie_accept_all")}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
